import { useCallback, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { SetterOrUpdater } from 'recoil'
import styled from 'styled-components'

import RefundTerm from '@/features/support/refund/components/RefundTerm'
import { AutocallForm, AutocallFormItem } from '@/shared/components/Common/form'
import AutocallBtn from '@/shared/components/Common/form/AutocallBtn'
import AutocallInput from '@/shared/components/Common/form/AutocallInput'
import AutocallSelector from '@/shared/components/Common/form/AutocallSelector'
import TelecomSelector from '@/shared/components/Common/form/TelecomSelector'
import { bankOptions } from '@/shared/constants'
import { TELECOM } from '@/shared/constants/common'
import STYLE_GUIDE from '@/shared/constants/styleGuide'
import { useValidate } from '@/shared/hooks'
import { useRefundAPI } from '@/shared/hooks/useRefundAPI.hook'
import {
  otpAuthType,
  otpCryptoNumber,
  otpPNumber,
  otpTelecom,
  otpTimeOut,
  otpTransctionID,
} from '@/shared/recoil/otp/atom'
import {
  refundAgreeRefund,
  refundBankContentsType,
  refundBantAccount,
  refundUName,
} from '@/shared/recoil/user/atom'
import { inputDisabled, validateTypeFam } from '@/shared/recoil/validate/atom'
import utils from '@/shared/utils'
import { useMonitoredTimer } from '@/shared/utils/sentry/timer'

const isValidTelecomCode = (code: string | undefined): code is string => {
  if (code === undefined) return false
  const n = parseInt(code, 10)
  return Number.isInteger(n) && n >= 0 && n < 3
}

const telecomFromCode = (code: string): Telecom => parseInt(code, 10) as Telecom

const RefundPage = () => {
  const navigate = useNavigate()
  const { telecomCode } = useParams()
  const [bankAccount, setBankAccount] = useRecoilState(refundBantAccount)
  const [uName, setUName] = useRecoilState(refundUName)
  const [bankType, setBankType] = useRecoilState(refundBankContentsType)
  const [pNumber, setPNumber] = useRecoilState(otpPNumber)
  const [pTelecom, setPTelecom] = useRecoilState(otpTelecom)
  const [certNum, setCertNum] = useRecoilState(otpCryptoNumber)
  const setAuthType = useSetRecoilState(otpAuthType)
  const [agree, setAgree] = useRecoilState(refundAgreeRefund)
  const [getOtpTimeOut, setOtpTimeOut] = useRecoilState(otpTimeOut)
  const [getOtpTransactionID, setOtpTransactionID] = useRecoilState(otpTransctionID)
  const refundBankTypeValidState = useRecoilState(validateTypeFam('refundBankType'))
  const refundAgreeValidState = useRecoilState(validateTypeFam('refundAgree'))
  const pNumValidState = useRecoilState(validateTypeFam('refundPNumber'))
  const refundBankAccountValidState = useRecoilState(validateTypeFam('refundBankAccount'))
  const refundUnameValidState = useRecoilState(validateTypeFam('refundUName'))
  const setPhoneInput = useSetRecoilState(inputDisabled('phoneInput'))
  const setTelecomInput = useSetRecoilState(inputDisabled('telecom'))
  const { replace, join } = useValidate()
  const { certNumber, phoneNumber } = join
  const { refundPNumberV, refundBankTypeV, refundBankAccountV, refundUNameV, refundAgreeV } =
    useValidate().refund
  const { replaceRefundName, replaceBankAccount, replacePNumber } = replace
  const numberRef = useRef<HTMLInputElement>(null)
  const bankAccountRef = useRef<HTMLInputElement>(null)
  const certRef = useRef<HTMLInputElement>(null)
  const nameRef = useRef<HTMLInputElement>(null)
  const [timeLeft, setTimeLeft] = useRecoilState(otpTimeOut)
  const { startTimer, stopTimer } = useMonitoredTimer()
  const refund = useRefundAPI()

  const setTelecomWithUrl = useCallback<SetterOrUpdater<Telecom>>(
    (value) => {
      const next = typeof value === 'function' ? value(pTelecom) : value
      if (next === pTelecom) return
      setPTelecom(next)
      setCertNum('')
      stopTimer()
      setOtpTimeOut(0)
      setOtpTransactionID('')
      setPhoneInput({ isDisabled: false })
      navigate(`/refund/${next}`, { replace: true })
    },
    [
      pTelecom,
      setPTelecom,
      setCertNum,
      setOtpTimeOut,
      setOtpTransactionID,
      setPhoneInput,
      navigate,
      stopTimer,
    ],
  )

  const executeOTP = () => {
    if (getOtpTimeOut > 0 && getOtpTransactionID !== '') {
      if (timeLeft !== 180 && timeLeft > 160) {
        console.log('none case')
      } else if (timeLeft === 180 || timeLeft <= 160) {
        phoneNumber.recoilState[1]({ valid: false, msg: '100' })
        setPhoneInput({ isDisabled: true })
        stopTimer()
        startTimer()
      } else {
        setPhoneInput({ isDisabled: false })
        certNumber.recoilState[1]({ valid: true, msg: '202' })
        phoneNumber.recoilState[1]({ valid: false, msg: '202' })
        setOtpTimeOut(0)
        setOtpTransactionID('')
      }
    } else {
      stopTimer()
      setTimeLeft(180)
      setPhoneInput({ isDisabled: false })
      setOtpTimeOut(0)
      setOtpTransactionID('')
    }
  }

  const submitRefundForm = async () => {
    if (!refundPNumberV.validFunc(pNumber)) {
      window.scrollTo({ top: 300, behavior: 'smooth' })
      return
    }
    if (!certNumber.validFunc(certNum)) {
      window.scrollTo({ top: 420, behavior: 'smooth' })
      return
    }
    if (!refundBankTypeV.validFunc(`${bankType}`)) {
      window.scrollTo({ top: 570, behavior: 'smooth' })
      return
    }
    if (!refundBankAccountV.validFunc(bankAccount)) {
      window.scrollTo({ top: 720, behavior: 'smooth' })
      return
    }
    if (!refundUNameV.validFunc(uName)) {
      window.scrollTo({ top: 870, behavior: 'smooth' })
      return
    }
    if (!refundAgreeV.validFunc(`${agree}`)) {
      window.scrollTo({ top: 990, behavior: 'smooth' })
      return
    }
    if (
      refundPNumberV.validFunc(pNumber) &&
      certNumber.validFunc(certNum) &&
      refundBankTypeV.validFunc(`${bankType}`) &&
      refundBankAccountV.validFunc(bankAccount) &&
      refundUNameV.validFunc(uName) &&
      refundAgreeV.validFunc(`${agree}`)
    ) {
      await refund.send()
    }
  }

  useEffect(() => {
    if (!isValidTelecomCode(telecomCode)) {
      navigate('/refund/0', { replace: true })
    }
  }, [telecomCode, navigate])

  useEffect(() => {
    if (isValidTelecomCode(telecomCode)) {
      const idx = telecomFromCode(telecomCode)
      setPTelecom(idx)
    }
  }, [telecomCode, setPTelecom])

  useEffect(() => {
    refundBankTypeValidState[1]({ valid: true, msg: '' })
    refundAgreeValidState[1]({ valid: true, msg: '' })
    pNumValidState[1]({ valid: true, msg: '' })
    refundBankAccountValidState[1]({ valid: true, msg: '' })
    refundUnameValidState[1]({ valid: true, msg: '' })
    setUName('')
    setPNumber('')
    setBankAccount('')
    setCertNum('')
    setAuthType('delete')
    setPhoneInput({ isDisabled: false })
    setTelecomInput({ isDisabled: false })
    setOtpTimeOut(0)
    setOtpTransactionID('')
    refund.terms().then()
  }, [])

  useEffect(() => {
    executeOTP()
  }, [getOtpTimeOut, getOtpTransactionID])

  return (
    <div>
      <RefundTitle>
        서비스 환불을 위해
        <br />
        본인인증이 필요합니다.
      </RefundTitle>
      <RefundMsgWrapper>
        <span>* 서비스 이용 이력이 없을 경우 환불이 가능합니다.</span>
      </RefundMsgWrapper>
      <AutocallForm>
        <AutocallFormItem title="통신사 선택" essential>
          <TelecomSelector telecom={pTelecom} setTelecom={setTelecomWithUrl} />
        </AutocallFormItem>
        <AutocallFormItem title="휴대폰 번호" validate={refundPNumberV.recoilState[0]} essential>
          <AutocallInput
            inputRef={numberRef}
            inputType="tel"
            placeholder="‘-’제외 하고 입력해주세요."
            state={utils.common.parsePNumber(pNumber)}
            setState={setPNumber}
            validate={refundPNumberV}
            // onKeyup={handleSendBtnClick}
            replacer={replacePNumber}
            maxLength={13}
          />
        </AutocallFormItem>
        <AutocallFormItem title="인증번호 입력" validate={certNumber.recoilState[0]} essential>
          <AutocallInput
            inputRef={certRef}
            inputType="tel"
            placeholder="인증번호를 입력해주세요."
            state={certNum}
            setState={setCertNum}
            validate={certNumber}
            // onKeyup={handleSendBtnClick}
            isCount
            timeRef={timeLeft}
            replacer={replacePNumber}
            maxLength={4}
          />
        </AutocallFormItem>
        <AutocallFormItem
          title="환불 받을 은행 선택"
          validate={refundBankTypeV.recoilState[0]}
          essential
        >
          <AutocallSelector
            state={bankType}
            setState={setBankType}
            placeholder="은행을 선택해주세요."
            options={bankOptions}
            validate={refundBankTypeV}
          />
        </AutocallFormItem>
        <AutocallFormItem
          title="계좌번호 입력"
          validate={refundBankAccountV.recoilState[0]}
          essential
        >
          <AutocallInput
            inputRef={bankAccountRef}
            inputType="tel"
            placeholder="계좌번호를 입력해주세요."
            state={bankAccount}
            setState={setBankAccount}
            validate={refundBankAccountV}
            // onKeyup={handleSendBtnClick}
            replacer={replaceBankAccount}
            maxLength={16}
          />
        </AutocallFormItem>
        <AutocallFormItem title="예금주명 입력" validate={refundUNameV.recoilState[0]} essential>
          <AutocallInput
            inputRef={nameRef}
            inputType="text"
            placeholder="예금주명을 입력해주세요."
            state={uName}
            setState={setUName}
            validate={refundUNameV}
            // onKeyup={handleSendBtnClick}
            replacer={replaceRefundName}
            maxLength={3000}
          />
        </AutocallFormItem>
        <AutocallFormItem title=" " validate={refundAgreeV.recoilState[0]}>
          <RefundTerm
            telecom={pTelecom}
            state={agree}
            setState={setAgree}
            validate={refundAgreeV}
          />
          {pTelecom === TELECOM.SKT && (
            <RefundCheckWrapper>
              ※환불 접수 시 운영정책에 따라 해당 번호의 서비스 재가입이 일정 기간 제한될 수
              있습니다.
            </RefundCheckWrapper>
          )}
        </AutocallFormItem>
        <AutocallFormItem>
          <div className="refund-submit">
            <AutocallBtn
              title={pTelecom === TELECOM.SKT ? '환불 접수' : '서비스 해지 후 환불 등록'}
              type="default"
              onClickFunc={submitRefundForm}
            />
            <div className="under-text">
              <p className="red">* 가입 당일 해지하신 고객님은 요금이 청구되지 않습니다.</p>
              <p className="red">* 서비스 이용요금은 매월 사용일 수 만큼 계산되어 청구됩니다.</p>
              <p>
                * 데이터유니버스는 고객의 환불 요청에 따른 조치를 이행하기 위하여 개인정보보호법 제
                15조 제1항 4호에 근거하여 개인정보를 수집 이용합니다. (환불처리 완료 후 3년간까지)
              </p>
            </div>
          </div>
        </AutocallFormItem>
      </AutocallForm>
    </div>
  )
}

export default RefundPage

const RefundMsgWrapper = styled.div`
  @media screen and (min-width: ${STYLE_GUIDE.SIZE.maxMobileSize}) {
    display: flex;
    width: 730px;
    padding: 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    flex-shrink: 0;
    border-radius: 10px;
    background: #f5f5f5;
    margin-bottom: 20px;
    > p {
      color: #2b3639;
      font-size: 20px;
      font-style: normal;
      font-weight: 700;
      line-height: 32px; /* 160% */
      margin: 0;
    }

    > span {
      color: #2b3639;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
      color: #f00;
    }
  }
  @media screen and (max-width: ${STYLE_GUIDE.SIZE.maxMobileSize}) {
    display: flex;
    width: 320px;
    padding: 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    border-radius: 10px;
    background: #f5f5f5;
    margin-bottom: 10px;

    > p {
      color: #2b3639;
      font-size: 16px;
      font-style: normal;
      font-weight: 700;
      line-height: 22px; /* 137.5% */
      margin: 0;
    }

    > span {
      color: #2b3639;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
      color: #f00;
    }
  }
`
const RefundTitle = styled.h3`
  font-size: 30px;
  font-weight: 700;
  line-height: 1.5;
  margin-bottom: 20px;
`
const RefundCheckWrapper = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  padding-left: 36px;
  .red {
    font-size: 14px;
    color: #ff3f33;
  }
  @media screen and (max-width: ${STYLE_GUIDE.SIZE.maxMobileSize}) {
    margin-top: 10px;
  }
`
